import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Search,
  MoreHorizontal,
  Tags,
  Loader2,
  Pencil,
  Trash2,
  Globe,
  ExternalLink,
  Building2,
} from 'lucide-react';
import { usePermission } from '@/components/guards/permission-guard';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { BrandModal } from '@/components/trading/brand-modal';
import { BrandSuppliersCard } from '@/components/trading/brand-suppliers-card';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface TradingBrand {
  id: string;
  code: string;
  name: string;
  slug: string;
  description?: string | null;
  logo?: string | null;
  website?: string | null;
  country?: string | null;
  isActive: boolean;
  sortOrder: number;
  _count: {
    models: number;
  };
}

export function TradingBrandsPage() {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const canCreate = usePermission('BRANDS', 'CREATE');
  const canEdit = usePermission('BRANDS', 'EDIT');
  const canDelete = usePermission('BRANDS', 'DELETE');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState<TradingBrand | undefined>();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState<string | null>(null);
  const [suppliersSheetOpen, setSuppliersSheetOpen] = useState(false);
  const [selectedBrandForSuppliers, setSelectedBrandForSuppliers] = useState<TradingBrand | null>(null);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
  };

  const { data, isLoading, error } = trpc.tradingBrand.list.useQuery({
    search: debouncedSearch || undefined,
  });

  const utils = trpc.useUtils();

  const deleteMutation = trpc.tradingBrand.delete.useMutation({
    onSuccess: () => {
      utils.tradingBrand.list.invalidate();
      showSuccessToast(t('common.deleted'));
      setDeleteConfirmOpen(false);
      setBrandToDelete(null);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const handleAddBrand = () => {
    setEditingBrand(undefined);
    setIsModalOpen(true);
  };

  const handleEditBrand = (brand: TradingBrand) => {
    setEditingBrand(brand);
    setIsModalOpen(true);
  };

  const handleDeleteBrand = (id: string) => {
    setBrandToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleManageSuppliers = (brand: TradingBrand) => {
    setSelectedBrandForSuppliers(brand);
    setSuppliersSheetOpen(true);
  };

  const confirmDelete = () => {
    if (brandToDelete) {
      deleteMutation.mutate(brandToDelete);
    }
  };

  const brands = data?.brands ?? [];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('brands.title')}</h1>
          <p className="text-muted-foreground">
            {t('brands.subtitle')}
            {brands.length > 0 && ` (${brands.length} ${tCommon('total')})`}
          </p>
        </div>
        {canCreate && (
          <Button onClick={handleAddBrand}>
            <Plus className="mr-2 h-4 w-4" />
            {t('brands.addBrand')}
          </Button>
        )}
      </div>

      {/* Brand Modal */}
      <BrandModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        brand={editingBrand}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={tCommon('delete')}
        description={t('brands.deleteConfirm')}
        confirmLabel={tCommon('delete')}
        variant="destructive"
        onConfirm={confirmDelete}
        isLoading={deleteMutation.isPending}
      />

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('brands.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="h-8 w-full border border-border bg-background pl-10 pr-4 text-sm focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20"
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">{t('common.loading')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('common.error')}</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && brands.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Tags className="h-12 w-12 text-muted-foreground/50" />
          <h3 className="mt-4 text-lg font-medium">{t('brands.noBrands')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {debouncedSearch ? t('brands.noBrands') : t('brands.noBrandsDesc')}
          </p>
          {!debouncedSearch && canCreate && (
            <Button className="mt-4" onClick={handleAddBrand}>
              <Plus className="mr-2 h-4 w-4" />
              {t('brands.addBrand')}
            </Button>
          )}
        </div>
      )}

      {/* Brands Grid */}
      {!isLoading && !error && brands.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {brands.map((brand) => (
            <div
              key={brand.id}
              className="group border border-border bg-card p-5 transition-shadow hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {brand.logo ? (
                    <img
                      src={brand.logo}
                      alt={brand.name}
                      className="h-12 w-12 object-contain"
                    />
                  ) : (
                    <div className="flex h-12 w-12 items-center justify-center bg-emerald-100">
                      <Tags className="h-6 w-6 text-emerald-600" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold">{brand.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('brands.code')}: {brand.code}
                    </p>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {canEdit && (
                      <>
                        <DropdownMenuItem onClick={() => handleEditBrand(brand)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          {tCommon('edit')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleManageSuppliers(brand)}>
                          <Building2 className="mr-2 h-4 w-4" />
                          {t('brands.suppliers.title')}
                        </DropdownMenuItem>
                      </>
                    )}
                    {canDelete && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => handleDeleteBrand(brand.id)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {tCommon('delete')}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <span
                  className={`px-2 py-0.5 text-xs font-medium ${
                    brand.isActive
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {brand.isActive ? t('brands.active') : t('brands.inactive')}
                </span>
              </div>

              {brand.description && (
                <p className="mt-3 text-sm text-muted-foreground line-clamp-2">
                  {brand.description}
                </p>
              )}

              <div className="mt-4 space-y-2 text-sm">
                {brand.country && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Globe className="h-3.5 w-3.5" />
                    <span>{brand.country}</span>
                  </div>
                )}
                {brand.website && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <ExternalLink className="h-3.5 w-3.5" />
                    <a
                      href={brand.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate hover:text-primary"
                    >
                      {brand.website}
                    </a>
                  </div>
                )}
              </div>

              <div className="mt-4 flex items-center justify-between border-t pt-4">
                <div className="text-sm text-muted-foreground">
                  {brand._count.models} {t('brands.models')}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Suppliers Sheet */}
      <Sheet open={suppliersSheetOpen} onOpenChange={setSuppliersSheetOpen}>
        <SheetContent className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{t('brands.suppliers.title')}</SheetTitle>
            <SheetDescription>
              {selectedBrandForSuppliers?.name}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            {selectedBrandForSuppliers && (
              <BrandSuppliersCard
                brandId={selectedBrandForSuppliers.id}
                brandName={selectedBrandForSuppliers.name}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
